import React from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Order } from "@/types/order";
import { OrderItemWithCurrency, useItems } from "./hooks";
import Header from "../table-header";
import { TableStates } from "../hooks";
import SideBar from "../table-sidebar";

interface Props {
  data: Order[];
  tableStates: TableStates;
}

const ItemsTable: React.FC<Props> = ({ data: orders, tableStates }) => {
  const {
    view,
    setView,
    sidebarOpen,
    setSidebarOpen,
    selectedOrder,
    setSelectedOrder,
  } = tableStates;

  const { columns, filteredItems } = useItems(orders);

  const handleRowClick = (order: OrderItemWithCurrency) => {
    setSelectedOrder(order);
    setSidebarOpen(true);
  };

  const table = useReactTable({
    data: filteredItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div>
      <Header table={table} setView={setView} view={view} />
      <Table>
        <TableHeader className="border-b border-slate-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="h-12">
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  onClick={() => handleRowClick(row.original)}
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="h-12 cursor-pointer transition-colors duration-200 ease-in-out hover:bg-slate-100"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <SideBar
        isOpen={sidebarOpen}
        data={selectedOrder}
        onClose={() => setSidebarOpen(false)}
        view={view}
      />
    </div>
  );
};

export default ItemsTable;
